import type {IceCandidateSummary, MediaEndpointResult, MediaRegionResult} from './types'
import {getRegionForCode} from './regions'

/** Matches Amazon Connect Endpoint Test Utility ICE_TIMEOUT. */
export const ICE_TIMEOUT_MS = 8000

export class IceTimeoutError extends Error {
  constructor(message = 'Timeout') {
    super(message)
    this.name = 'IceTimeoutError'
    Object.setPrototypeOf(this, IceTimeoutError.prototype)
  }
}

type CandidateCallback = (candidate: IceCandidateSummary) => void

/**
 * STUN gather against a Connect TurnNlb host on UDP 3478.
 * Success = first srflx or relay candidate within ICE_TIMEOUT (same as AWS tool).
 */
export const gatherIceCandidates = (
  turnHost: string,
  onCandidate: CandidateCallback,
  timeoutMs: number = ICE_TIMEOUT_MS
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof RTCPeerConnection === 'undefined') {
      reject(new Error('RTCPeerConnection not supported'))
      return
    }

    let pc: RTCPeerConnection | null = new RTCPeerConnection({
      iceServers: [{urls: [`stun:${turnHost}:3478`]}],
      iceTransportPolicy: 'all',
    })

    let foundCandidate = false
    let settled = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const tearDown = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      if (!pc) return
      try {
        pc.removeEventListener('icecandidate', onIceCandidate)
        pc.removeEventListener('icecandidateerror', onIceCandidateError)
        pc.removeEventListener('icegatheringstatechange', onGatheringStateChange)
        pc.close()
      } catch {
        // ignore
      }
      pc = null
    }

    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      tearDown()
      fn()
    }

    const onIceCandidate = (event: RTCPeerConnectionIceEvent) => {
      const candidate = event.candidate
      if (!candidate?.candidate) return
      const type = candidate.type
      if (type && ['srflx', 'relay'].includes(type)) {
        foundCandidate = true
        onCandidate({
          type,
          protocol: candidate.protocol || '',
          address: candidate.address || '',
        })
        finish(() => resolve())
      }
    }

    const onIceCandidateError = (_event: Event) => {
      // AWS tool logs ICE candidate errors but does not fail the probe on them alone.
    }

    const onGatheringStateChange = () => {
      if (!pc || pc.iceGatheringState !== 'complete') return
      if (foundCandidate) {
        finish(() => resolve())
      } else {
        finish(() => reject(new IceTimeoutError('Timeout')))
      }
    }

    timeoutId = setTimeout(() => {
      if (foundCandidate) {
        finish(() => resolve())
      } else {
        finish(() => reject(new IceTimeoutError('Timeout')))
      }
    }, timeoutMs)

    try {
      pc.createDataChannel('amazon-connect-endpoint-test-utility')
      pc.addEventListener('icecandidate', onIceCandidate)
      pc.addEventListener('icecandidateerror', onIceCandidateError)
      pc.addEventListener('icegatheringstatechange', onGatheringStateChange)

      void pc
        .createOffer({offerToReceiveAudio: true} as RTCOfferOptions)
        .then((offer) => pc?.setLocalDescription(offer))
        .catch((err) => finish(() => reject(err)))
    } catch (err) {
      finish(() => reject(err instanceof Error ? err : new Error(String(err))))
    }
  })
}

export const probeMediaEndpoint = async (endpoint: string): Promise<MediaEndpointResult> => {
  const candidates: IceCandidateSummary[] = []
  const pushCandidate = (candidate: IceCandidateSummary) => {
    if (!candidates.some((c) => c.address === candidate.address && c.type === candidate.type)) {
      candidates.push(candidate)
    }
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return {
      endpoint,
      candidates,
      success: false,
      timedOut: false,
      error: 'Not online',
    }
  }

  try {
    await gatherIceCandidates(endpoint, pushCandidate)
    return {
      endpoint,
      candidates,
      success: true,
      timedOut: false,
      error: null,
    }
  } catch (err) {
    if (err instanceof IceTimeoutError) {
      return {
        endpoint,
        candidates,
        success: false,
        timedOut: true,
        error: 'Timeout',
      }
    }
    return {
      endpoint,
      candidates,
      success: false,
      timedOut: false,
      error: err instanceof Error ? err.toString() : String(err),
    }
  }
}

export const runMediaDiagnostics = async (regionCodes: string[]): Promise<MediaRegionResult[]> => {
  const results: MediaRegionResult[] = []

  for (const code of regionCodes) {
    const region = getRegionForCode(code)
    if (!region) continue

    // Probe TurnNlb hosts in parallel — success criteria unchanged vs AWS sequential tool.
    const endpoints = await Promise.all(
      region.mediaEndpoints.map((host) => probeMediaEndpoint(host))
    )

    results.push({
      code: region.code,
      name: region.name,
      success: endpoints.length > 0 && endpoints.every((e) => e.success),
      endpoints,
    })
  }

  return results
}
